const contact = "Contact many@pawluna.art with your order number and enough detail for us to review your request.";

export const policyGroups = [
  {
    title: "Shopping and Orders",
    items: [
      {
        path: "order-policy",
        title: "Order & Cancellation Policy",
        intro: "This policy explains how Pawluna receives, reviews, accepts, changes, and cancels online orders.",
        sections: [
          {
            title: "Order submission and acceptance",
            paragraphs: [
              "Submitting checkout is an offer to buy the items shown in your order. An automated receipt confirms that we received the request; a binding sale is formed only when we send an acceptance or dispatch confirmation.",
              "We may decline an order before acceptance because of stock, pricing or description errors, shipping restrictions, payment authorization, suspected fraud, or legal requirements. Any captured amount for a declined order will be returned to the original payment method.",
            ],
          },
          {
            title: "Changes and cancellations",
            paragraphs: [
              "Request a correction or cancellation as soon as possible. We cannot promise changes after fulfillment begins, and an order already dispatched must follow the Returns Policy.",
              "We do not substitute a product without consent. If only part of an order is unavailable, we may cancel and refund that item while fulfilling the remainder unless you ask us not to.",
            ],
          },
          {
            title: "Order records and contact",
            paragraphs: [
              "Keep your confirmation email and check names, quantities, delivery address, and contact details promptly. Customers are responsible for accurate delivery information, subject to rights that cannot be waived by law.",
              contact,
            ],
          },
        ],
      },
      {
        path: "payment-policy",
        title: "Payment & Billing Policy",
        intro: "This policy describes payment authorization, billing information, payment security, and refunds.",
        sections: [
          {
            title: "Accepted payment methods",
            paragraphs: [
              "Available methods are displayed at checkout and may vary by device, order value, or location. A payment method must be valid and authorized for use by the person placing the order.",
              "We may use a third-party payment processor. Pawluna does not intentionally store complete card numbers or card security codes on its own application servers.",
            ],
          },
          {
            title: "Authorization and charges",
            paragraphs: [
              "Your provider may place a temporary authorization hold when an order is submitted. The timing of capture and release is controlled in part by the payment provider and financial institution.",
              "You authorize us and our processor to charge the total disclosed at checkout, including any displayed shipping and taxes. Your provider may impose separate currency-conversion, overdraft, or other fees.",
            ],
          },
          {
            title: "Billing problems and refunds",
            paragraphs: [
              "If a charge fails, we may ask you to use another available method. We will never ask you to send a full card number or security code by email.",
              "Approved refunds are issued to the original payment method unless law requires another method. Posting time depends on the payment provider. For an unrecognized charge, contact us and your provider promptly.",
            ],
          },
        ],
      },
      {
        path: "pricing-promotions",
        title: "Pricing & Promotions Policy",
        intro: "This policy explains displayed prices, discounts, promotion rules, and corrections.",
        sections: [
          {
            title: "Prices and corrections",
            paragraphs: [
              "Unless stated otherwise, prices are displayed in U.S. dollars. The checkout total controls when it differs from a cart estimate because of shipping, tax, location, or an expired promotion.",
              "We try to keep pricing accurate. If a material pricing error is discovered before acceptance, we may contact you for approval at the correct price or cancel and refund the affected item.",
            ],
          },
          {
            title: "Promotion conditions",
            paragraphs: [
              "A promotion is subject to its stated dates, eligibility, inventory, exclusions, and redemption limit. Codes have no cash value and cannot be sold, copied for commercial distribution, or combined unless expressly allowed.",
              "We may reject misuse, automated redemption, duplicate accounts created to bypass limits, or a code obtained through unauthorized channels. Lawful consumer rights are not affected.",
            ],
          },
          {
            title: "Returns from promotional orders",
            paragraphs: [
              "A refund reflects the amount actually paid for the returned item after allocated discounts. If a return causes an order to stop qualifying for a bundle or threshold offer, the refund may be adjusted as disclosed by the offer and permitted by law.",
            ],
          },
        ],
      },
      {
        path: "taxes-duties",
        title: "Taxes, Customs & Duties Policy",
        intro: "This policy explains how sales tax, import charges, and customs responsibilities may apply.",
        sections: [
          {
            title: "Sales and similar taxes",
            paragraphs: [
              "Applicable sales, use, value-added, or similar taxes may be calculated from the product, delivery destination, and legal requirements in effect at checkout.",
              "A tax estimate can change if the delivery address or order changes. If you claim an exemption, you must provide valid documentation before purchase where our checkout supports it.",
            ],
          },
          {
            title: "International import charges",
            paragraphs: [
              "Unless checkout explicitly states that duties are prepaid, the recipient is responsible for import duties, brokerage fees, customs charges, and local taxes assessed at destination.",
              "Customs authorities may require identifying or order information. A refusal to pay destination charges may result in delay, return, disposal, and deductions from any refund where lawful and reasonably incurred.",
            ],
          },
        ],
      },
      {
        path: "preorders-backorders",
        title: "Preorders & Backorders Policy",
        intro: "This policy applies only when a product page clearly offers an item before it is ready to ship or permits a backorder.",
        sections: [
          {
            title: "Clear identification",
            paragraphs: [
              "A preorder or backorder will be labeled before purchase with the best available release or dispatch estimate. Ordinary product listings should not be treated as preorder offers.",
              "Release and replenishment dates are estimates and can move because of manufacturing, inspection, transport, or customs events.",
            ],
          },
          {
            title: "Payment and mixed orders",
            paragraphs: [
              "The product page or checkout will indicate when payment is collected. Items in a mixed order may ship together or separately, and any additional charge will be disclosed before submission.",
              "If a delay is material, we will provide updated information and any cancellation or refund rights required by applicable law.",
            ],
          },
        ],
      },
      {
        path: "warranty",
        title: "Product Warranty Policy",
        intro: "This policy explains how Pawluna handles product defects and any product-specific written warranty.",
        sections: [
          {
            title: "Defects and legal rights",
            paragraphs: [
              "Products should arrive as described and free from defects in materials and workmanship. Any mandatory warranty, conformity, or consumer remedy available under applicable law remains unaffected.",
              "A longer or additional written warranty applies only when it is stated on the product page or supplied with the product, and its stated duration and conditions will control.",
            ],
          },
          {
            title: "What is not a manufacturing defect",
            paragraphs: [
              "Normal wear, cosmetic change, misuse, pet chewing beyond the product's intended use, improper cleaning, unauthorized modification, accidental damage, or failure to follow care and supervision instructions is not ordinarily a manufacturing defect.",
              "No pet product is indestructible. Stop use if a product is damaged or presents a swallowing, choking, or injury risk.",
            ],
          },
          {
            title: "Making a claim",
            paragraphs: [
              "Send proof of purchase, a description of the issue, and clear photographs to many@pawluna.art. Do not discard an allegedly defective item until we confirm whether inspection or return is needed.",
              "A valid claim may be resolved by repair, replacement, refund, or another remedy required by law. This policy does not create warranties beyond those expressly stated or legally required.",
            ],
          },
        ],
      },
    ],
  },
  {
    title: "Products and Safety",
    items: [
      {
        path: "product-safety",
        title: "Product Safety & Care Policy",
        intro: "This policy provides general safety, supervision, cleaning, and responsible-use guidance for Pawluna pet enrichment products.",
        sections: [
          {
            title: "Choose and supervise",
            paragraphs: [
              "Choose a product suited to the animal's species, size, age, health, feeding style, and behavior. Introduce enrichment gradually and supervise use, especially with a new pet or product.",
              "Inspect before and after use. Remove the product immediately if it is cracked, torn, has loose parts, becomes small enough to swallow, or otherwise presents a hazard.",
            ],
          },
          {
            title: "Food and cleaning",
            paragraphs: [
              "Use only foods and cleaning methods compatible with the product label. Wash before first use and after food contact; dry fully before storage. Replace porous, deeply scratched, or difficult-to-clean items.",
              "Avoid ingredients that are unsafe for your pet. Pawluna does not provide veterinary or nutritional advice; ask a veterinarian about allergies, medical diets, swallowing issues, dental conditions, or unusual behavior.",
            ],
          },
          {
            title: "Incident reporting",
            paragraphs: [
              "If an injury or safety incident may involve a product, stop use and seek appropriate veterinary attention. Preserve the product and packaging, record the batch or order information, and report the incident to many@pawluna.art.",
            ],
          },
        ],
      },
      {
        path: "product-recalls",
        title: "Product Recall & Safety Notice Policy",
        intro: "This policy explains how Pawluna communicates and responds to a product safety correction or recall.",
        sections: [
          {
            title: "Safety review",
            paragraphs: [
              "We review credible reports involving injury, contamination, labeling, material failure, or other safety concerns. We may request the item, packaging, photographs, order details, and information about how the product was used.",
              "Where appropriate, we will cooperate with suppliers, laboratories, marketplaces, carriers, insurers, and government authorities while limiting personal data to what is relevant.",
            ],
          },
          {
            title: "Recall communications",
            paragraphs: [
              "A recall or safety notice will identify affected products as precisely as reasonably possible, describe the risk, and state whether to stop use, dispose of, return, repair, or replace the product.",
              "Notices may appear on our website and be sent to customers using order contact information. Customers should keep order emails and follow instructions in the notice rather than sending an item without authorization.",
            ],
          },
          {
            title: "Remedies",
            paragraphs: [
              "The available remedy will depend on the safety action and applicable law and may include replacement, repair, refund, or safe disposal instructions. Recall remedies do not reduce non-waivable consumer rights.",
            ],
          },
        ],
      },
      {
        path: "user-content",
        title: "Reviews & User Content Policy",
        intro: "This policy applies when a customer voluntarily submits a review, photograph, message, testimonial, or other content to Pawluna.",
        sections: [
          {
            title: "Your responsibility",
            paragraphs: [
              "Submit only content you created or have permission to use. Content must be honest, relevant, lawful, and must not contain confidential information, personal data of others, hate, threats, harassment, malware, deceptive claims, or intellectual-property infringement.",
              "Do not submit veterinary claims as fact, incentivized endorsements without disclosure, or content that identifies a child without appropriate permission.",
            ],
          },
          {
            title: "Permission to use submitted content",
            paragraphs: [
              "You keep ownership of your content. By deliberately submitting it for publication, you grant Pawluna a non-exclusive, worldwide, royalty-free license to host, reproduce, format, and display it for operating and promoting Pawluna, subject to applicable law and any written campaign terms.",
              "We may moderate, decline, or remove content but do not promise to monitor every submission. Email many@pawluna.art to report a rights issue or request review of content associated with you.",
            ],
          },
        ],
      },
    ],
  },
  {
    title: "Privacy and Technology",
    items: [
      {
        path: "state-privacy",
        title: "U.S. State Privacy Notice",
        intro: "This notice supplements the Privacy Policy for residents of U.S. states that provide additional privacy rights.",
        sections: [
          {
            title: "Personal information practices",
            paragraphs: [
              "Depending on your interaction, Pawluna may process identifiers, contact and delivery details, transaction history, device or network information, communications, preferences, and inferences used for customer service, security, fulfillment, and site operation.",
              "We do not knowingly sell personal information for money. We do not currently use cross-context behavioral advertising on this site. If those practices change, we will update this notice and provide legally required choices.",
            ],
          },
          {
            title: "Available rights",
            paragraphs: [
              "Depending on your state and subject to exceptions, you may request access, confirmation, correction, deletion, portability, or an appeal, and may have rights to opt out of sale, targeted advertising, or certain profiling.",
              "Submit a request to many@pawluna.art. We may verify identity and authority, limit the response to protect others, or deny a request where law permits. We will explain an appeal method when one is legally required.",
            ],
          },
          {
            title: "Non-discrimination and authorized agents",
            paragraphs: [
              "We will not unlawfully discriminate because you exercised a privacy right. An authorized agent must provide proof of authority, and we may also confirm the request directly with the consumer where permitted.",
            ],
          },
        ],
      },
      {
        path: "children-privacy",
        title: "Children's Privacy Notice",
        intro: "Pawluna's store is intended for adults purchasing pet products and is not directed to children.",
        sections: [
          {
            title: "Age and collection",
            paragraphs: [
              "We do not knowingly collect personal information online from children under 13 in the United States, or below a higher minimum age where local law applies, without legally valid parental authorization.",
              "A person placing an order must be legally capable of entering the transaction or act with appropriate adult involvement.",
            ],
          },
          {
            title: "Parent or guardian requests",
            paragraphs: [
              "If you believe a child provided personal information, email many@pawluna.art with enough detail to locate it. We will review the request, verify authority where appropriate, and delete or restrict the data when required.",
              "Please do not send unnecessary information about a child in the initial message.",
            ],
          },
        ],
      },
      {
        path: "marketing-communications",
        title: "Marketing Communications Policy",
        intro: "This policy explains how Pawluna handles optional promotional email and service communications.",
        sections: [
          {
            title: "Promotional messages",
            paragraphs: [
              "We send promotional email when we have the consent or other lawful basis required for the recipient. Signing up is optional, and consent to marketing is not a condition of purchasing ordinary products.",
              "Promotions may be tailored using information you provided and limited interaction data described in the Privacy Policy, but we do not promise that every offer will be available to every recipient.",
            ],
          },
          {
            title: "Unsubscribe and service messages",
            paragraphs: [
              "Use an unsubscribe link where provided or email many@pawluna.art. We may retain a suppression record so that we honor the request. Processing may take the period allowed by applicable law.",
              "An opt-out from promotions does not stop essential order, safety, security, account, or policy notices connected to an existing transaction or legal duty.",
            ],
          },
        ],
      },
      {
        path: "ai-data-processing",
        title: "AI & Automated Data Processing Policy",
        intro: "This policy describes Pawluna's principles for any use of artificial intelligence or automated tools in store operations.",
        sections: [
          {
            title: "Permitted uses",
            paragraphs: [
              "We may use automated tools to assist with fraud screening, search, categorization, translation, drafting, analytics, accessibility, or customer-service triage. Human review may be used where accuracy, safety, or customer rights call for it.",
              "We do not present automated output as veterinary advice and do not intentionally use solely automated processing to make a decision with legal or similarly significant effects unless disclosed and lawfully supported.",
            ],
          },
          {
            title: "Data safeguards and accuracy",
            paragraphs: [
              "Personal data used with a service provider is limited to a relevant purpose under contractual, security, and retention controls appropriate to the service. Confidential payment credentials should not be entered into a general-purpose AI tool.",
              "Automated output can be incomplete or incorrect. Customers should rely on confirmed order details, product labels, safety instructions, and qualified professional advice where appropriate.",
            ],
          },
          {
            title: "Questions and challenges",
            paragraphs: [
              "If you believe an automated process materially affected your order or privacy rights, email many@pawluna.art. We will review available records and provide any human review or appeal required by law.",
            ],
          },
        ],
      },
      {
        path: "security",
        title: "Security & Vulnerability Disclosure Policy",
        intro: "This policy outlines Pawluna's security approach and a good-faith channel for reporting suspected vulnerabilities.",
        sections: [
          {
            title: "Security practices",
            paragraphs: [
              "We use administrative, technical, and organizational safeguards proportionate to the nature of the store and data. No website or transmission method can be guaranteed completely secure.",
              "Customers should use trusted devices and networks, verify the site address, protect order emails, and report suspicious messages. Pawluna will not request a password or full payment-card security code by email.",
            ],
          },
          {
            title: "Good-faith reporting",
            paragraphs: [
              "Report a suspected vulnerability to many@pawluna.art with reproducible steps, affected URL, likely impact, and non-sensitive evidence. Allow reasonable time for investigation before public disclosure.",
              "Do not access another person's data, disrupt service, use social engineering, perform denial-of-service testing, plant malware, demand payment, or exceed the minimum testing needed to demonstrate the issue.",
            ],
          },
          {
            title: "Response",
            paragraphs: [
              "We will acknowledge and prioritize credible reports as resources permit. This policy does not create a bug-bounty program, promise payment, or authorize conduct prohibited by law.",
            ],
          },
        ],
      },
    ],
  },
  {
    title: "Website and Legal",
    items: [
      {
        path: "acceptable-use",
        title: "Acceptable Use Policy",
        intro: "This policy protects Pawluna visitors, services, and systems from misuse.",
        sections: [
          {
            title: "Prohibited conduct",
            paragraphs: [
              "Do not use the site for unlawful, fraudulent, deceptive, harassing, infringing, or harmful activity. Do not attempt unauthorized access, probe accounts or systems without permission, evade security, introduce malicious code, or interfere with availability.",
              "Do not scrape at a rate that burdens the service, use automated purchasing to defeat fair limits, impersonate another person, submit false order information, or reuse content beyond the permission given in our Intellectual Property Policy.",
            ],
          },
          {
            title: "Enforcement",
            paragraphs: [
              "We may rate-limit, block, preserve evidence, cancel affected transactions, or report suspected misconduct when reasonably necessary and lawful. Enforcement will be proportionate to risk and does not limit remedies available under law.",
            ],
          },
        ],
      },
      {
        path: "accessibility",
        title: "Accessibility Statement",
        intro: "Pawluna aims to make its online store usable by people with a wide range of abilities, devices, and assistive technologies.",
        sections: [
          {
            title: "Our approach",
            paragraphs: [
              "We work toward recognized accessibility practices for structure, keyboard use, readable contrast, meaningful labels, alternative text, responsive layouts, and reduced-motion preferences where applicable.",
              "Accessibility is an ongoing process. Third-party payment, map, font, or embedded services may have features outside our direct control.",
            ],
          },
          {
            title: "Feedback and assistance",
            paragraphs: [
              "If you encounter a barrier, email many@pawluna.art and identify the page, task, device, browser, and assistive technology if you are comfortable doing so. We will try to provide the information or service through a reasonable alternative.",
              "Requests are handled with respect for privacy. Please do not include health information unless it is necessary for the accommodation you request.",
            ],
          },
        ],
      },
      {
        path: "intellectual-property",
        title: "Intellectual Property Policy",
        intro: "This policy explains ownership of Pawluna site materials and how to report alleged infringement.",
        sections: [
          {
            title: "Pawluna materials",
            paragraphs: [
              "The Pawluna name, original site text, layout, graphics, photographs, product presentation, and other materials are owned by or licensed to Pawluna and may be protected by copyright, trademark, design, and other laws.",
              "You may view and make ordinary personal use of the site for shopping. No right is granted to reproduce, sell, publish, remove rights notices, train a commercial model on protected content, or create commercial derivatives except with written permission or as law permits.",
            ],
          },
          {
            title: "Respect for third-party rights",
            paragraphs: [
              "Pawluna does not claim ownership of third-party names, marks, payment symbols, or customer content. Such material belongs to its respective owner and is used only where licensed, permitted, or necessary for identification.",
              "We expect users and suppliers to provide only content they are entitled to provide.",
            ],
          },
          {
            title: "Infringement notices",
            paragraphs: [
              "Send a notice to many@pawluna.art identifying the protected work or mark, the allegedly infringing material and URL, your contact details, your basis for acting, and a good-faith statement. We may request legally required declarations before acting.",
              "We will review sufficiently detailed notices and may remove or restrict material while investigating. Knowingly false reports may create liability.",
            ],
          },
        ],
      },
      {
        path: "third-party-services",
        title: "Third-Party Services & Links Policy",
        intro: "This policy explains the role of outside providers and websites linked from or used by the Pawluna store.",
        sections: [
          {
            title: "Service providers",
            paragraphs: [
              "We may rely on payment processors, carriers, hosting, communications, security, analytics, and other providers to operate the store. Providers receive only information relevant to their service and act under their own terms or our service agreement, as applicable.",
              "Availability and processing time can depend on those providers. Pawluna remains responsible for its own obligations and does not waive non-excludable consumer rights.",
            ],
          },
          {
            title: "External links",
            paragraphs: [
              "A link to an external website is provided for convenience or attribution and does not by itself mean endorsement. External sites control their content, security, availability, cookies, and privacy practices.",
              "Review the destination address and relevant policies before providing data or making a separate purchase. Report a broken, misleading, or unsafe link to many@pawluna.art.",
            ],
          },
        ],
      },
      {
        path: "complaints-disputes",
        title: "Complaints & Dispute Resolution Policy",
        intro: "This policy describes how to raise a concern and how Pawluna seeks to resolve disputes fairly.",
        sections: [
          {
            title: "Making a complaint",
            paragraphs: [
              "Email many@pawluna.art with your order number, a clear description, relevant dates, and the outcome you seek. Avoid sending full card details, passwords, or unrelated sensitive information.",
              "We aim to acknowledge complaints promptly, investigate relevant records, and provide a reasoned response. Complex cases, carrier inquiries, chargebacks, or safety reviews may require additional time.",
            ],
          },
          {
            title: "Escalation and informal resolution",
            paragraphs: [
              "If you disagree with the first response, reply with the issue you believe was missed and request a further review. Both sides should make a good-faith effort to resolve the matter informally before formal proceedings where appropriate.",
              "Nothing in this policy removes mandatory rights to contact a regulator, consumer-protection body, payment provider, court, or other authorized forum.",
            ],
          },
        ],
      },
    ],
  },
  {
    title: "Responsible Business",
    items: [
      {
        path: "ethical-sourcing",
        title: "Ethical Sourcing & Modern Slavery Policy",
        intro: "This policy states Pawluna's expectations concerning forced labor, child labor, trafficking, and responsible supply chains.",
        sections: [
          {
            title: "Standards",
            paragraphs: [
              "We do not knowingly accept forced labor, human trafficking, debt bondage, unlawful child labor, confiscation of identity documents, recruitment-fee coercion, or retaliation against workers who raise good-faith concerns.",
              "Suppliers are expected to follow applicable labor, wage, hour, health, safety, anti-discrimination, environmental, and anti-corruption laws and to communicate comparable expectations to relevant subcontractors.",
            ],
          },
          {
            title: "Risk review and response",
            paragraphs: [
              "Our approach may include supplier questions, contractual expectations, documentation review, risk-based follow-up, and corrective-action requests proportionate to our size and influence.",
              "Where a credible concern arises, we favor protecting affected people and responsible remediation. Serious, repeated, or uncorrected violations may lead to suspension or termination of the relationship.",
            ],
          },
          {
            title: "Reporting",
            paragraphs: [
              "Concerns may be sent confidentially to many@pawluna.art. Include facts rather than speculation and do not put yourself or another person at risk to collect evidence. We prohibit retaliation within relationships we control.",
            ],
          },
        ],
      },
      {
        path: "sustainability",
        title: "Sustainability Policy",
        intro: "This policy describes Pawluna's practical approach to product life, materials, packaging, and environmental claims.",
        sections: [
          {
            title: "Priorities",
            paragraphs: [
              "We consider durability, safe repeated use, responsible material choices, efficient packaging, transport impacts, and waste reduction when commercially and technically feasible.",
              "Customers can support longer product life by following care instructions, using products as intended, repairing only where safe, and checking locally available recycling or disposal options.",
            ],
          },
          {
            title: "Environmental claims",
            paragraphs: [
              "A material percentage, recycled-content statement, recyclability statement, or certification should be read exactly as stated for the specific product or component. It does not mean the entire product or lifecycle has no environmental impact.",
              "Recycling acceptance differs by location and facility. We aim to use specific, supportable language and will correct a claim if better evidence becomes available.",
            ],
          },
          {
            title: "Continuous improvement",
            paragraphs: [
              "Sustainability involves tradeoffs among safety, durability, sanitation, cost, and logistics. We review improvements over time rather than claiming perfection. Questions or substantiated suggestions are welcome at many@pawluna.art.",
            ],
          },
        ],
      },
      {
        path: "anti-fraud",
        title: "Anti-Fraud & Abuse Policy",
        intro: "This policy explains measures Pawluna may take to protect customers and the store from fraudulent or abusive transactions.",
        sections: [
          {
            title: "Review indicators",
            paragraphs: [
              "We may review mismatched billing and delivery information, repeated failed payments, unusual order velocity, high-risk routing, promotion abuse, account takeover indicators, chargeback history, or information supplied by payment and security providers.",
              "A signal does not by itself establish wrongdoing. We seek to use proportionate review and may ask for a safer payment method or limited verification.",
            ],
          },
          {
            title: "Protective action",
            paragraphs: [
              "We may delay, limit, decline, or cancel a transaction before acceptance where reasonably necessary to manage fraud, security, legal, or safety risk. We may preserve relevant records and cooperate with providers or lawful authorities.",
              "Do not email full card details or unnecessary identity documents. If an order was declined in error or you suspect account misuse, contact many@pawluna.art.",
            ],
          },
        ],
      },
    ],
  },
];

export const policyCatalog = policyGroups.flatMap((group) => group.items);

export const policyByPath = policyCatalog.reduce((catalog, policy) => {
  catalog[policy.path] = policy;
  return catalog;
}, {});
